// Adapted from holybible's services/bible.ts — in-memory fetch + parse, no IndexedDB.
declare const BIBLE_DATA_BASE: string;

const dataUrl = (p: string) => BIBLE_DATA_BASE + "/" + p;

// 书卷名归一化：罗马数字前缀（I Kings）与阿拉伯数字前缀（1 Kings）互相匹配
const ROMAN_PREFIX: Record<string, string> = { I: "1", II: "2", III: "3" };
export const normalizeBook = (book: string): string => {
  const b = String(book || "").trim();
  const m = b.match(/^(I|II|III)\s+(.+)$/);
  return m ? `${ROMAN_PREFIX[m[1]]} ${m[2]}` : b;
};

export interface BibleVersion {
  id: string;
  name: string;
  language: string;
  languageName: string;
  url: string;
  bookUrl: string;
  footnoteUrl?: string;
  outlineUrl?: string;
  topicUrl?: string;
}

export interface BibleBook {
  _id: string;
  book_id: string;
  name: string;
  acronym_name: string;
}

export interface BibleVerse {
  _id: string;
  Book: string;
  Chapter: string;
  Verse: string;
  Flag: string;
  Text: string;
}

export interface BibleFootnote {
  _id: string;
  book_index: string;
  chapter: string;
  section: string;
  flag: string;
  location: string;
  seq: string;
  note: string;
}

export interface BibleOutline {
  _id: string;
  book_index: string;
  chapter: string;
  section: string;
  flag: string;
  level: string;
  outline: string;
}

export interface BibleTopic {
  _id: string;
  book_index: string;
  topic: string;
}

export interface BibleSupplemental {
  footnotes: BibleFootnote[];
  outlines: BibleOutline[];
  topics: BibleTopic[];
}

const BIBLE_VERSIONS: BibleVersion[] = [
  { id: "ChiRcVs", name: "恢复注解版(简体)", language: "zh", languageName: "中文", url: dataUrl("zh/ChiRcVs/ChiRcVs.csv"), bookUrl: dataUrl("zh/ChiRcVs/book_name.csv"), footnoteUrl: dataUrl("zh/ChiRcVs/footnote.csv"), outlineUrl: dataUrl("zh/ChiRcVs/outline.csv"), topicUrl: dataUrl("zh/ChiRcVs/topic.csv") },
  { id: "ChiUns", name: "和合本(简体)", language: "zh", languageName: "中文", url: dataUrl("zh/ChiUns/ChiUns.csv"), bookUrl: dataUrl("zh/ChiUns/book_name.csv") },
  { id: "ChiRcV", name: "恢復註解版(繁體)", language: "zh-hant", languageName: "中文", url: dataUrl("zh-hant/ChiRcV/ChiRcV.csv"), bookUrl: dataUrl("zh-hant/ChiRcV/book_name.csv"), footnoteUrl: dataUrl("zh-hant/ChiRcV/footnote.csv"), outlineUrl: dataUrl("zh-hant/ChiRcV/outline.csv"), topicUrl: dataUrl("zh-hant/ChiRcV/topic.csv") },
  { id: "ChiUn", name: "和合本(繁體)", language: "zh-hant", languageName: "中文", url: dataUrl("zh-hant/ChiUn/ChiUn.csv"), bookUrl: dataUrl("zh-hant/ChiUn/book_name.csv") },
  { id: "CzeBKR", name: "Bible Kralická", language: "cs", languageName: "čeština", url: dataUrl("cs/CzeBKR/CzeBKR.csv"), bookUrl: dataUrl("cs/CzeBKR/book_name.csv") },
  { id: "DaOT1871NT1907", name: "Den danske bibel 1871/1907", language: "da", languageName: "dansk", url: dataUrl("da/DaOT1871NT1907/DaOT1871NT1907.csv"), bookUrl: dataUrl("da/DaOT1871NT1907/book_name.csv") },
  { id: "GerElb1905", name: "Elberfelder Bibel 1905", language: "de", languageName: "Deutsch", url: dataUrl("de/GerElb1905/GerElb1905.csv"), bookUrl: dataUrl("de/GerElb1905/book_name.csv") },
  { id: "GreVamvas", name: "Ελληνική μετάφραση", language: "el", languageName: "Ελληνικά", url: dataUrl("el/GreVamvas/GreVamvas.csv"), bookUrl: dataUrl("el/GreVamvas/book_name.csv") },
  { id: "KJV", name: "KJV", language: "en", languageName: "English", url: dataUrl("en/KJV/KJV.csv"), bookUrl: dataUrl("en/KJV/book_name.csv") },
  { id: "Darby", name: "Darby Bible", language: "en", languageName: "English", url: dataUrl("en/Darby/Darby.csv"), bookUrl: dataUrl("en/Darby/book_name.csv") },
  { id: "ASV", name: "American Standard Version", language: "en", languageName: "English", url: dataUrl("en/ASV/ASV.csv"), bookUrl: dataUrl("en/ASV/book_name.csv") },
  { id: "SpaRV", name: "Reina-Valera", language: "es", languageName: "español", url: dataUrl("es/SpaRV/SpaRV.csv"), bookUrl: dataUrl("es/SpaRV/book_name.csv") },
  { id: "FinPR", name: "Pyhä Raamattu", language: "fi", languageName: "suomi", url: dataUrl("fi/FinPR/FinPR.csv"), bookUrl: dataUrl("fi/FinPR/book_name.csv") },
  { id: "FreSegond1910", name: "La Segond 1910", language: "fr", languageName: "français", url: dataUrl("fr/FreSegond1910/FreSegond1910.csv"), bookUrl: dataUrl("fr/FreSegond1910/book_name.csv") },
  { id: "HunKar", name: "Károli Gáspár-féle fordítás", language: "hu", languageName: "magyar", url: dataUrl("hu/HunKar/HunKar.csv"), bookUrl: dataUrl("hu/HunKar/book_name.csv") },
  { id: "ItaRive", name: "Riva 1861", language: "it", languageName: "italiano", url: dataUrl("it/ItaRive/ItaRive.csv"), bookUrl: dataUrl("it/ItaRive/book_name.csv") },
  { id: "JapKougo", name: "口語訳聖書", language: "ja", languageName: "日本語", url: dataUrl("ja/JapKougo/JapKougo.csv"), bookUrl: dataUrl("ja/JapKougo/book_name.csv") },
  { id: "KorRV", name: "개역한글판 성경", language: "ko", languageName: "한국어", url: dataUrl("ko/KorRV/korRV.csv"), bookUrl: dataUrl("ko/KorRV/book_name.csv") },
  { id: "LtKBB", name: "Lietuviškas Krikščionio Biblinis Balsas", language: "lt", languageName: "lietuvių kalba", url: dataUrl("lt/LtKBB/LtKBB.csv"), bookUrl: dataUrl("lt/LtKBB/book_name.csv") },
  { id: "LvGluck8", name: "Glika Bībele 8. izdevums", language: "lv", languageName: "latviešu valoda", url: dataUrl("lv/LvGluck8/LvGluck8.csv"), bookUrl: dataUrl("lv/LvGluck8/book_name.csv") },
  { id: "BurJudson", name: "ျမန္မာက်မ္း (ဂ်ဒ်ဆင္)", language: "my", languageName: "မြန်မာစာ", url: dataUrl("my/BurJudson/BurJudson.csv"), bookUrl: dataUrl("my/BurJudson/book_name.csv") },
  { id: "DutSVV", name: "Statenvertaling", language: "nl", languageName: "Nederlands", url: dataUrl("nl/DutSVV/DutSVV.csv"), bookUrl: dataUrl("nl/DutSVV/book_name.csv") },
  { id: "Norsk", name: "Norsk", language: "no", languageName: "Norsk", url: dataUrl("no/Norsk/Norsk.csv"), bookUrl: dataUrl("no/Norsk/book_name.csv") },
  { id: "PolUGdanska", name: "Uwspółcześniona Biblia Gdańska", language: "pl", languageName: "polski", url: dataUrl("pl/PolUGdanska/PolUGdanska.csv"), bookUrl: dataUrl("pl/PolUGdanska/book_name.csv") },
  { id: "PorNVA", name: "Bíblia Nova Versão de Acesso Livre", language: "pt", languageName: "português", url: dataUrl("pt/PorNVA/PorNVA.csv"), bookUrl: dataUrl("pt/PorNVA/book_name.csv") },
  { id: "RusSynodal", name: "Синодальный перевод", language: "ru", languageName: "русский язык", url: dataUrl("ru/RusSynodal/RusSynodal.csv"), bookUrl: dataUrl("ru/RusSynodal/book_name.csv") },
  { id: "SloChraska", name: "Sveto pismo Starega in Novega zakona", language: "sl", languageName: "slovenščina", url: dataUrl("sl/SloChraska/SloChraska.csv"), bookUrl: dataUrl("sl/SloChraska/book_name.csv") },
  { id: "Alb", name: "Bibla e Shenjtë", language: "sq", languageName: "shqip", url: dataUrl("sq/Alb/Alb.csv"), bookUrl: dataUrl("sq/Alb/book_name.csv") },
  { id: "SweFolk", name: "Svenska Folkbibeln", language: "sv", languageName: "svenska", url: dataUrl("sv/SweFolk/SweFolk.csv"), bookUrl: dataUrl("sv/SweFolk/book_name.csv") },
  { id: "Viet", name: "tiếng Việt", language: "vi", languageName: "tiếng Việt", url: dataUrl("vi/Viet/Viet.csv"), bookUrl: dataUrl("vi/Viet/book_name.csv") },
];

export const getAvailableVersions = (): BibleVersion[] => BIBLE_VERSIONS;
export const getVersionById = (id: string): BibleVersion | undefined => BIBLE_VERSIONS.find((v) => v.id === id);
import { resolveBibleDefaults } from "./bibleLocaleDefaults";
export const getDefaultVersionId = (locale: string): string => resolveBibleDefaults(locale).versionId;

// ---- Simple CSV parsing ----
function parseRows(text: string, expectedColumns: number): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  const out: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
    if (parts.length > expectedColumns) {
      const fixed = parts.slice(0, expectedColumns - 1);
      const tail = parts.slice(expectedColumns - 1).join(",");
      out.push([...fixed, tail]);
    } else {
      out.push(parts);
    }
  }
  return out;
}

// 处理带引号逗号与空字段的 CSV 行（恢复注解版注解文本内含逗号）
function splitCSVLine(line: string): string[] {
  const parts: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      parts.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  parts.push(cur.trim());
  return parts;
}

// 按表头解析经文章节 CSV（兼容 5 列普通版与 7 列恢复注解版）
function parseVerses(text: string): BibleVerse[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [];
  const header = splitCSVLine(lines[0]).map((c) => c.toLowerCase());
  const idx = (name: string) => header.findIndex((c) => c === name);
  const idIdx = idx("_id");
  const bookIdx = idx("book") >= 0 ? idx("book") : idx("book_id");
  const chapterIdx = idx("chapter");
  const verseIdx = idx("verse");
  const flagIdx = idx("flag");
  const textIdx = idx("text") >= 0 ? idx("text") : idx("content");
  const get = (parts: string[], n: number) => (n >= 0 && n < parts.length ? parts[n] : "");
  return lines
    .slice(1)
    .map((l) => {
      const r = splitCSVLine(l);
      return {
        _id: get(r, idIdx),
        Book: get(r, bookIdx),
        Chapter: get(r, chapterIdx),
        Verse: get(r, verseIdx),
        Flag: get(r, flagIdx) || "0",
        Text: get(r, textIdx),
      };
    })
    .filter((v) => v.Verse !== "" && v.Book !== "");
}

function parseSupplemental(text: string, kind: "footnote"): BibleFootnote[];
function parseSupplemental(text: string, kind: "outline"): BibleOutline[];
function parseSupplemental(text: string, kind: "topic"): BibleTopic[];
function parseSupplemental(text: string, kind: "footnote" | "outline" | "topic"): BibleFootnote[] | BibleOutline[] | BibleTopic[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return [] as BibleFootnote[] | BibleOutline[] | BibleTopic[];
  const header = splitCSVLine(lines[0]).map((c) => c.toLowerCase());
  const idx = (name: string) => header.findIndex((c) => c === name);
  const get = (parts: string[], n: number) => (n >= 0 && n < parts.length ? parts[n] : "");
  const rows = lines.slice(1).map((l) => splitCSVLine(l));
  if (kind === "footnote") {
    return rows
      .map((r) => ({
        _id: get(r, idx("_id")),
        book_index: get(r, idx("book_index")),
        chapter: get(r, idx("chapter")),
        section: get(r, idx("section")),
        flag: get(r, idx("flag")),
        location: get(r, idx("location")),
        seq: get(r, idx("seq")),
        note: get(r, idx("note")),
      }))
      .filter((f) => f._id !== "") as BibleFootnote[];
  }
  if (kind === "outline") {
    return rows
      .map((r) => ({
        _id: get(r, idx("_id")),
        book_index: get(r, idx("book_index")),
        chapter: get(r, idx("chapter")),
        section: get(r, idx("section")),
        flag: get(r, idx("flag")),
        level: get(r, idx("level")),
        outline: get(r, idx("outline")),
      }))
      .filter((o) => o._id !== "") as BibleOutline[];
  }
  return rows
    .map((r) => ({
      _id: get(r, idx("_id")),
      book_index: get(r, idx("book_index")),
      topic: get(r, idx("topic")),
    }))
    .filter((t) => t._id !== "") as BibleTopic[];
}

const BOOK_COLUMNS = 4; // _id,book_id,name,acronym_name

// ---- In-memory cache with a small LRU bound ----
const cache = new Map<string, unknown>();
const CACHE_MAX = 6;
function cached<T>(key: string, loader: () => Promise<T>): Promise<T> {
  const hit = cache.get(key) as T | undefined;
  if (hit !== undefined) {
    cache.delete(key);
    cache.set(key, hit);
    return Promise.resolve(hit);
  }
  return loader().then((val) => {
    cache.set(key, val);
    if (cache.size > CACHE_MAX) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) cache.delete(oldest);
    }
    return val;
  });
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${url} (${res.status})`);
  return res.text();
}

export async function loadBibleMetadata(versionId: string): Promise<{ books: BibleBook[] }> {
  const version = getVersionById(versionId);
  if (!version) throw new Error(`Unknown Bible version: ${versionId}`);
  return cached(`meta:${versionId}`, async () => {
    const text = await fetchText(version.bookUrl);
    const rows = parseRows(text, BOOK_COLUMNS);
    const books: BibleBook[] = rows.map((r) => ({
      _id: r[0] || "",
      book_id: r[1] || "",
      name: r[2] || "",
      acronym_name: r[3] || "",
    }));
    return { books };
  });
}

async function loadAllVerses(versionId: string): Promise<BibleVerse[]> {
  const version = getVersionById(versionId);
  if (!version) throw new Error(`Unknown Bible version: ${versionId}`);
  return cached(`verses:${versionId}`, async () => {
    const text = await fetchText(version.url);
    return parseVerses(text);
  });
}

// Resolve a book reference (English name, number, localized name) to a book entry.
async function resolveBook(versionId: string, book: string): Promise<BibleBook | null> {
  const meta = await loadBibleMetadata(versionId);
  const books = meta.books || [];
  if (!books.length) return null;
  const norm = normalizeBook(book);
  const isPureNum = /^\d+$/.test(norm);
  let entry = books.find(
    (b) =>
      b.book_id === book ||
      b.name === book ||
      b.acronym_name === book ||
      b._id === book ||
      b.book_id === norm ||
      b.name === norm ||
      b.acronym_name === norm
  );
  if (!entry && isPureNum) {
    const num = parseInt(norm, 10);
    if (num > 0 && num <= books.length) entry = books[num - 1];
  }
  return entry || null;
}

export async function loadChapter(versionId: string, book: string, chapter: number): Promise<BibleVerse[]> {
  const entry = await resolveBook(versionId, book);
  if (!entry) return [];
  const verses = await loadAllVerses(versionId);
  const bookNum = parseInt(entry.book_id, 10) || parseInt(entry._id, 10) || 0;
  return verses.filter((v) => {
    const vBookPureNum = /^\d+$/.test(String(v.Book || "").trim());
    const bookMatches = vBookPureNum
      ? parseInt(v.Book, 10) === bookNum
      : v.Book === entry.book_id ||
        v.Book === entry.name ||
        v.Book === entry.acronym_name ||
        v.Book === entry._id ||
        normalizeBook(v.Book) === normalizeBook(entry.book_id) ||
        normalizeBook(v.Book) === normalizeBook(entry.name) ||
        normalizeBook(v.Book) === normalizeBook(entry.acronym_name);
    return bookMatches && parseInt(v.Chapter, 10) === chapter;
  });
}

// Load a list of per-chapter ranges (book/chapter/verseStart/verseEnd) with dedupe.
export interface VerseRangeRef {
  book: string;
  chapter: number;
  verseStart: number;
  verseEnd: number;
  halfStart?: string;
  halfEnd?: string;
}

export interface LoadedVerse extends BibleVerse {
  bookName: string;
  reference: string;
  // 整本圣经 1-66 卷序号，用于跨版本/美图页等按卷号引用的场景（与 holybible 的 ordinalBookId 一致）
  bookNumber: number;
}

export async function loadVerseRanges(versionId: string, ranges: VerseRangeRef[]): Promise<LoadedVerse[]> {
  const seen = new Set<string>();
  const out: LoadedVerse[] = [];
  const meta = await loadBibleMetadata(versionId);
  for (const range of ranges) {
    const verses = await loadChapter(versionId, range.book, range.chapter);
    for (const v of verses) {
      const num = parseInt(v.Verse, 10);
      if (num < range.verseStart || num > range.verseEnd) continue;
      const key = `${range.book}|${range.chapter}|${num}|${v.Flag || "0"}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ...v,
        bookName: getBookName(meta, v.Book),
        reference: `${getBookName(meta, v.Book)} ${v.Chapter}:${num}`,
        bookNumber: getBookNumber(meta, v.Book),
      });
    }
  }
  return out;
}

export const isRecoveryVersionId = (id: string): boolean => id === "ChiRcVs" || id === "ChiRcV";

export async function loadSupplemental(versionId: string): Promise<BibleSupplemental> {
  const version = getVersionById(versionId);
  if (!version) return { footnotes: [], outlines: [], topics: [] };
  return cached(`supp:${versionId}`, async () => {
    const [footnotes, outlines, topics] = await Promise.all([
      version.footnoteUrl
        ? fetchText(version.footnoteUrl).then((t) => parseSupplemental(t, "footnote"))
        : Promise.resolve([] as BibleFootnote[]),
      version.outlineUrl
        ? fetchText(version.outlineUrl).then((t) => parseSupplemental(t, "outline"))
        : Promise.resolve([] as BibleOutline[]),
      version.topicUrl
        ? fetchText(version.topicUrl).then((t) => parseSupplemental(t, "topic"))
        : Promise.resolve([] as BibleTopic[]),
    ]);
    return { footnotes, outlines, topics };
  });
}

const ENGLISH_BOOK_TO_NUM: Record<string, number> = {
  Genesis: 1, Exodus: 2, Leviticus: 3, Numbers: 4, Deuteronomy: 5,
  Joshua: 6, Judges: 7, Ruth: 8, "I Samuel": 9, "II Samuel": 10,
  "I Kings": 11, "II Kings": 12, "I Chronicles": 13, "II Chronicles": 14,
  Ezra: 15, Nehemiah: 16, Esther: 17, Job: 18,
  Psalms: 19, Proverbs: 20, Ecclesiastes: 21, "Song of Solomon": 22,
  Isaiah: 23, Jeremiah: 24, Lamentations: 25, Ezekiel: 26, Daniel: 27,
  Hosea: 28, Joel: 29, Amos: 30, Obadiah: 31, Jonah: 32,
  Micah: 33, Nahum: 34, Habakkuk: 35, Zephaniah: 36, Haggai: 37,
  Zechariah: 38, Malachi: 39,
  Matthew: 40, Mark: 41, Luke: 42, John: 43, Acts: 44,
  Romans: 45, "I Corinthians": 46, "II Corinthians": 47, Galatians: 48,
  Ephesians: 49, Philippians: 50, Colossians: 51,
  "I Thessalonians": 52, "II Thessalonians": 53,
  "I Timothy": 54, "II Timothy": 55, Titus: 56, Philemon: 57,
  Hebrews: 58, James: 59, "I Peter": 60, "II Peter": 61,
  "I John": 62, "II John": 63, "III John": 64, Jude: 65,
  "Revelation of John": 66,
};

export const getBookName = (bible: { books: BibleBook[] }, bookId: string): string => {
  if (!bookId) return "";
  const books = bible.books || [];
  if (!books.length) return bookId;
  const norm = normalizeBook(bookId);
  const direct = books.find(
    (b) =>
      normalizeBook(b.book_id) === norm ||
      normalizeBook(b.name) === norm ||
      normalizeBook(b.acronym_name) === norm ||
      normalizeBook(b._id) === norm
  );
  if (direct) return direct.name || direct.book_id;
  const pure = /^\d+$/.test(norm);
  let num = pure ? parseInt(norm, 10) : 0;
  if (num <= 0) num = ENGLISH_BOOK_TO_NUM[norm] || ENGLISH_BOOK_TO_NUM[bookId] || 0;
  if (num > 0) {
    const book = books.find((b) => parseInt(b.book_id, 10) === num || parseInt(b._id, 10) === num);
    if (book) return book.name || book.book_id;
    if (num <= books.length) return books[num - 1].name || books[num - 1].book_id;
  }
  return bookId;
};

// 返回整本圣经中的卷序号 1–66（与 holybible 的 ordinalBookId 一致），
// 供打开 LuminaVerse 美图页等按卷号引用的跨版本场景使用。
export const getBookNumber = (bible: { books: BibleBook[] }, bookId: string): number => {
  if (!bookId) return 0;
  const books = bible.books || [];
  const norm = normalizeBook(bookId);
  const directIdx = books.findIndex(
    (b) =>
      normalizeBook(b.book_id) === norm ||
      normalizeBook(b.name) === norm ||
      normalizeBook(b.acronym_name) === norm ||
      normalizeBook(b._id) === norm
  );
  if (directIdx >= 0) return directIdx + 1;
  const isPureNum = /^\d+$/.test(norm);
  let num = isPureNum ? parseInt(norm, 10) : 0;
  if (num <= 0) num = ENGLISH_BOOK_TO_NUM[norm] || ENGLISH_BOOK_TO_NUM[bookId] || 0;
  if (num > 0 && (books.length === 0 || num <= books.length)) return num;
  return 0;
};
